import React, { useState, useEffect } from "react";
import { Section } from "../StructureElements/Section";
import { SectionHeaderTypography } from "../Typographies/SectionHeaderTypography";
import { ExplainTypography } from "../Typographies/ExplainTypography";
import { ManufacturerAutoComplete } from "../autocompletes/manufacturer-auto.complete";
import { Manufacturer } from "../../../core/models/entities/Manufacturer";
import { OrganizationManufacturerAccessDto } from "../../../data/models/responses/OrganizationManufacturerAccessDto";
import { useApiClient } from "../../../data/clients/ApiClient";
import { useSuccessToast, useErrorToast } from "../Toast/Toast";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { SectionHeader } from "../../screens/my-profile/SectionHeader";
import { SectionContent } from "../../screens/my-profile/SectionContent";

interface RestrictedManufacturerAccessControlProps {
  organizationId: number;
  manufacturers: Manufacturer[];
}

export function RestrictedManufacturerAccessControl({
  organizationId,
  manufacturers,
}: RestrictedManufacturerAccessControlProps) {
  const apiClient = useApiClient();
  const [accessData, setAccessData] =
    useState<OrganizationManufacturerAccessDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadAccessData();
  }, [organizationId]);

  const loadAccessData = async () => {
    try {
      const manufacturerAccessData =
        await apiClient.getOrganizationManufacturerAccess(organizationId);

      /*console.log(
        "Raw API response restricted:",
        JSON.stringify(manufacturerAccessData, null, 2)
      );*/

      // For restricted manufacturers, we need to include ALL restricted manufacturers
      // and set their hasAccess based on the saved data
      const allRestrictedManufacturers = manufacturers.filter(
        (m) => m.isRestricted
      );

      /*console.log(
        "All restricted manufacturers:",
        JSON.stringify(allRestrictedManufacturers, null, 2)
      );*/

      // The API response is the array itself, not nested under a manufacturers property
      // Cast to any[] to handle the actual API response structure
      const savedManufacturers =
        (manufacturerAccessData as unknown as any[]) || [];

      /*console.log(
        "Saved manufacturers from API:",
        JSON.stringify(savedManufacturers, null, 2)
      );*/

      const restrictedAccessData = {
        accessMode: "Whitelist" as "Whitelist", // Restricted manufacturers always use whitelist logic
        manufacturers: allRestrictedManufacturers.map((manufacturer) => {
          // Find the saved access data for this manufacturer
          const savedAccess = savedManufacturers.find(
            (m: any) => m.id === manufacturer.id
          );

          /*console.log(
            `Manufacturer ${manufacturer.id} (${manufacturer.name}): savedAccess=`,
            savedAccess
          );*/

          // If we have saved access data, use it. Otherwise, default to false.
          // Note: The API might return access data for manufacturers that are not restricted,
          // but we only care about restricted manufacturers in this component.
          const hasAccess = savedAccess ? savedAccess.hasAccess : false;

          /*console.log(
            `Setting hasAccess to ${hasAccess} for manufacturer ${manufacturer.id}`
          );*/

          return {
            id: manufacturer.id,
            name: manufacturer.name,
            hasAccess: hasAccess,
            isRestricted: true,
          };
        }),
      };

      //console.log("Processed restricted access data:", restrictedAccessData);

      setAccessData(restrictedAccessData);
    } catch (error) {
      console.error(
        "Error loading restricted manufacturer access data:",
        error
      );
      useErrorToast("Failed to load restricted manufacturer access settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManufacturerSelect = async (
    manufacturer: Manufacturer | null
  ) => {
    if (!manufacturer || !accessData) return;

    // For restricted manufacturers, always set hasAccess to true when selected
    const newHasAccess = true;

    // Update local state only - no API calls until Save Changes is clicked
    // Check if manufacturer already exists in access data
    const existingIndex = (accessData.manufacturers || []).findIndex(
      (m) => m.id === manufacturer.id
    );

    let updatedManufacturers = [...(accessData.manufacturers || [])];

    if (existingIndex >= 0) {
      // Update existing manufacturer access
      updatedManufacturers[existingIndex] = {
        ...updatedManufacturers[existingIndex],
        hasAccess: newHasAccess,
      };
    } else {
      // Add new manufacturer with access
      updatedManufacturers.push({
        id: manufacturer.id,
        name: manufacturer.name,
        hasAccess: newHasAccess,
        isRestricted: true,
      });
    }

    setAccessData({
      ...accessData,
      manufacturers: updatedManufacturers,
    });
    setHasChanges(true);
  };

  const handleManufacturerRemove = async (
    manufacturer: Manufacturer | null
  ) => {
    if (!manufacturer || !accessData) return;

    // Update local state only - no API calls until Save Changes is clicked
    // For restricted manufacturers, we set hasAccess: false when removing
    const updatedManufacturers = (accessData.manufacturers || []).map((m) =>
      m.id === manufacturer.id ? { ...m, hasAccess: false } : m
    );

    setAccessData({
      ...accessData,
      manufacturers: updatedManufacturers,
    });
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!accessData) return;

    setIsSaving(true);
    try {
      // Get current access data to compare
      const currentAccessData =
        await apiClient.getOrganizationManufacturerAccess(organizationId);

      // For ALL restricted manufacturers, we need to set the correct hasAccess value
      // Restricted manufacturers always use whitelist logic: selected = hasAccess: true
      const restrictedManufacturersList = manufacturers.filter(
        (m) => m.isRestricted
      );

      for (const manufacturer of restrictedManufacturersList) {
        // Determine if this restricted manufacturer should have access
        const shouldHaveAccess = (accessData.manufacturers || []).some(
          (m) => m.id === manufacturer.id && m.hasAccess
        );

        // Find current access status for this manufacturer
        const currentAccess = (currentAccessData.manufacturers || []).find(
          (m) => m.id === manufacturer.id
        );

        // Only update if the access status needs to change
        if (!currentAccess || currentAccess.hasAccess !== shouldHaveAccess) {
          await apiClient.updateOrganizationManufacturerAccess(
            organizationId,
            manufacturer.id,
            shouldHaveAccess
          );
        }
      }

      useSuccessToast("Restricted manufacturer changes saved successfully");
      setHasChanges(false);
    } catch (error) {
      useErrorToast("Failed to save restricted manufacturer changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    loadAccessData(); // Reload original data
    setHasChanges(false);
  };

  if (isLoading) {
    return <div></div>;
  }

  if (!accessData) {
    return <div></div>;
  }

  // Filter to only show restricted manufacturers
  const restrictedManufacturers = manufacturers.filter((m) => m.isRestricted);

  // Get all manufacturers that have access data
  const manufacturersWithAccess = accessData.manufacturers || [];

  // Available restricted manufacturers are those that don't have access yet
  // Use the original manufacturers array to preserve the guid property
  const availableRestrictedManufacturers = restrictedManufacturers.filter(
    (m) => !manufacturersWithAccess.some((am) => am.id === m.id && am.hasAccess)
  );

  // For restricted manufacturers, we only show those with hasAccess: true
  // (since restricted manufacturers always use whitelist logic)
  // Use the original manufacturers array to preserve the guid property
  const includedRestrictedManufacturers = restrictedManufacturers.filter((m) =>
    manufacturersWithAccess.some((am) => am.id === m.id && am.hasAccess)
  );

  return (
    <Section>
      <SectionHeader
        isCloseable={true}
        initialState={isOpen}
        onToggle={(open) => setIsOpen(open)}
      >
        <SectionHeaderTypography>
          Restricted Manufacturers
        </SectionHeaderTypography>
      </SectionHeader>

      <SectionContent close={!isOpen}>
        <div className={"flex-1 overflow-y-scroll space-y-4 mb-10"}>
          <ExplainTypography>
            Restricted manufacturers are special manufacturers that require
            explicit approval. <br />
            When granted access, these manufacturers will be available to the
            vendor regardless of the general access mode settings.
          </ExplainTypography>

          {/* Restricted Manufacturer Selection */}
          <div>
            <ExplainTypography>
              SELECT RESTRICTED MANUFACTURERS:
            </ExplainTypography>
            <div className="w-72 mt-2">
              <ManufacturerAutoComplete
                manufacturers={availableRestrictedManufacturers}
                onSelected={handleManufacturerSelect}
              />
            </div>
          </div>

          {/* Restricted Manufacturers List */}
          {includedRestrictedManufacturers.length > 0 && (
            <div>
              <ExplainTypography>GRANTED ACCESS TO:</ExplainTypography>
              <div className="flex flex-wrap mt-2">
                {includedRestrictedManufacturers.map((manufacturer) => (
                  <ManufacturerChip
                    key={manufacturer.id}
                    manufacturer={manufacturer}
                    onRemove={() => {
                      // Find the full manufacturer object from the original array
                      const fullManufacturer = manufacturers.find(
                        (m) => m.id === manufacturer.id
                      );
                      if (fullManufacturer) {
                        handleManufacturerRemove(fullManufacturer);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Save/Cancel Buttons */}
          <div className="flex space-x-2">
            <PrimaryButton onClick={handleSaveChanges} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </PrimaryButton>
            <SecondaryButton onClick={handleCancelChanges}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      </SectionContent>
    </Section>
  );
}

interface ManufacturerChipProps {
  manufacturer: { id: number; name: string; isRestricted?: boolean };
  onRemove: () => void;
}

function ManufacturerChip({ manufacturer, onRemove }: ManufacturerChipProps) {
  return (
    <div className="text-xs bg-gray-100 rounded px-2 py-1 mr-2 mb-2 flex items-center space-x-1">
      <span>{manufacturer.name}</span>
      {manufacturer.isRestricted && (
        <span className="text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
          Restricted
        </span>
      )}
      <button
        onClick={onRemove}
        className="text-gray-500 hover:text-gray-700"
        title="Remove access"
      >
        ×
      </button>
    </div>
  );
}
